import { Component, Inject, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar, MatDialog } from '@angular/material';
import * as L from 'leaflet';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmacionComponent } from 'src/app/home/confirmacion/confirmacion.component';

/**
 * Dialog para asignar una cita lejana a un tecnico.
 *
 * Flujo:
 *  1. Abre con data = { id_registro, id_tecnico, nombre_tecnico }
 *  2. Llama al backend top_citas_tecnico(id_tecnico) para traer las 3 citas
 *     candidatas + ubicacion del tecnico.
 *  3. Renderiza mapa Leaflet con marcador del tecnico + 3 marcadores de citas.
 *  4. Click en marcador de cita muestra detalle + boton "Asignar".
 *  5. Al asignar: backend UPDATE condicionado a status_id=1. Si ya fue tomada
 *     por otro agente, refresca las 3 citas.
 */
@Component({
  selector: 'app-asignar-cita-lejana-dialog',
  templateUrl: './asignar-cita-lejana-dialog.component.html',
  styleUrls: ['./asignar-cita-lejana-dialog.component.scss']
})
export class AsignarCitaLejanaDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('mapaContainer') mapaContainer: ElementRef;

  private map: L.Map;
  private markersLayer: L.LayerGroup;

  citas: any[] = [];
  citasMapa: any[] = [];  // Pendientes adicionales (status_id=1) para pintar
  tecnico: { lat: any, lng: any } = null;
  citaSeleccionada: any = null;

  // Filtros del mapa — multi-seleccion
  // 41 = Instalaciones (Orden), 44 = Reparaciones (Falla)
  filtroTipoOrden: number[] = [41, 44];
  filtroBanda: string[] = ['AM', 'PM'];

  tiposOrdenOpciones = [
    { value: 41, label: 'Instalación' },
    { value: 44, label: 'Reparación' }
  ];
  bandasOpciones = ['AM', 'PM'];
  cargando = false;
  asignando = false;
  cancelando = false;
  anulando = false;
  esperando = false;

  constructor(
    public dialogRef: MatDialogRef<AsignarCitaLejanaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private gateway: GatewayService,
    private snackbar: MatSnackBar,
    private spinner: NgxSpinnerService,
    private localSave: LocalSaveService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.cargarTopCitas();
  }

  ngAfterViewInit() {
    // Inicializa el mapa una vez haya elemento en DOM.
    setTimeout(() => this.initMap(), 100);
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }

  private initMap(): void {
    if (!this.mapaContainer) { return; }
    const container = this.mapaContainer.nativeElement;
    this.map = L.map(container).setView([9.0, -79.5], 12);  // Panama centro
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);
    this.markersLayer = L.layerGroup().addTo(this.map);
    // Si ya llegaron datos, dibuja
    if (this.citas.length || this.tecnico) {
      this.pintarMarcadores();
    }
  }

  cargarTopCitas() {
    this.cargando = true;
    this.citaSeleccionada = null;
    this.gateway.get('autoAsignLejTopCitas', { id_tecnico: this.data.id_tecnico })
      .then((res: any) => {
        this.citas = Array.isArray(res.citas) ? res.citas : [];
        this.tecnico = res.tecnico || null;
        this.cargando = false;
        if (this.map) { this.pintarMarcadores(); }
        // Al cargar candidatas, recarga tambien las pendientes del mapa
        this.cargarCitasMapa();
      })
      .catch(() => {
        this.cargando = false;
        this.snackbar.open('Error al cargar citas candidatas', 'Cerrar', { duration: 3000 });
      });
  }

  /**
   * Carga las citas PENDIENTES (status_id=1) del dia para pintarlas como
   * markers adicionales en el mapa. Aplica filtros de tipo_orden y banda
   * horaria y excluye las ids que ya estan como candidatas top.
   */
  cargarCitasMapa() {
    const excludeIds = this.citas.map(c => Number(c.id_cita)).filter(v => !!v);
    this.gateway.get('autoAsignLejCitasMapa', {
      tipos_orden: this.filtroTipoOrden,
      bandas: this.filtroBanda,
      excludeIds: excludeIds
    }).then((res: any) => {
      this.citasMapa = Array.isArray(res.citas) ? res.citas : [];
      if (this.map) { this.pintarMarcadores(); }
    }).catch(() => {
      // silencioso — no es critico para el flujo
    });
  }

  /** El usuario cambio un filtro del mapa → recargar */
  onFiltroMapaChange() {
    this.cargarCitasMapa();
  }

  private pintarMarcadores(): void {
    if (!this.map || !this.markersLayer) { return; }
    this.markersLayer.clearLayers();

    const bounds: L.LatLngExpression[] = [];

    // Marcador tecnico (pin azul con icono de persona/ingeniero)
    if (this.tecnico && this.tecnico.lat && this.tecnico.lng) {
      const lat = Number(this.tecnico.lat);
      const lng = Number(this.tecnico.lng);
      const iconTec = L.divIcon({
        className: 'marker-tecnico',
        html: '<div class="marker-tec-pin">' +
              '  <div class="marker-tec-circle">' +
              '    <i class="material-icons">engineering</i>' +
              '  </div>' +
              '  <div class="marker-tec-stem"></div>' +
              '</div>',
        iconSize: [40, 52],
        iconAnchor: [20, 48]
      });
      L.marker([lat, lng], { icon: iconTec, zIndexOffset: 500 })
        .bindTooltip('Técnico: ' + (this.data.nombre_tecnico || ''), { permanent: false })
        .addTo(this.markersLayer);
      bounds.push([lat, lng]);
    }

    // Marcadores citas candidatas (naranja con numero, resaltado verde si seleccionada)
    this.citas.forEach((cita, i) => {
      const lat = Number(cita.lat_cita);
      const lng = Number(cita.lng_cita);
      if (isNaN(lat) || isNaN(lng)) { return; }
      const seleccionada = this.citaSeleccionada
        && Number(this.citaSeleccionada.id_cita) === Number(cita.id_cita);
      const iconCita = L.divIcon({
        className: 'marker-cita',
        html: '<div class="marker-dot cita ' + (seleccionada ? 'sel' : '') + '"><span>' + (i + 1) + '</span></div>',
        iconSize: seleccionada ? [32, 32] : [24, 24],
        iconAnchor: seleccionada ? [16, 16] : [12, 12]
      });
      // Falla = id_contrata 44, Orden = id_contrata 41.
      // El numero mostrado es numero_orden si existe, si no id_cita.
      const prefijo = Number(cita.id_contrata) === 41 ? 'Orden #' : 'Falla #';
      const numero = cita.numero_orden || cita.id_cita;
      const etiqueta = prefijo + numero;
      const m = L.marker([lat, lng], { icon: iconCita, zIndexOffset: seleccionada ? 1000 : 0 })
        .bindTooltip(etiqueta, { permanent: false });
      m.on('click', () => this.seleccionarCita(cita));
      m.addTo(this.markersLayer);
      bounds.push([lat, lng]);
    });

    // Marcadores citas pendientes adicionales (instalacion naranja / reparacion morado)
    this.citasMapa.forEach(cita => {
      const lat = Number(cita.lat_cita);
      const lng = Number(cita.lng_cita);
      if (isNaN(lat) || isNaN(lng)) { return; }
      const seleccionada = this.citaSeleccionada
        && Number(this.citaSeleccionada.id_cita) === Number(cita.id_cita);
      const esInstalacion = Number(cita.id_contrata) === 41;
      const esPM = String(cita.am_pm || '').toUpperCase() === 'PM';
      const tipoClass = esInstalacion ? 'inst' : 'rep';
      const bandaClass = esPM ? 'pm' : 'am';
      const iconCita = L.divIcon({
        className: 'marker-pendiente',
        html: '<div class="marker-pend ' + tipoClass + ' ' + bandaClass
              + (seleccionada ? ' sel' : '') + '"></div>',
        iconSize: seleccionada ? [22, 22] : [16, 16],
        iconAnchor: seleccionada ? [11, 11] : [8, 8]
      });
      const prefijo = esInstalacion ? 'Orden #' : 'Falla #';
      const numero = cita.numero_orden || cita.id_cita;
      const etiqueta = prefijo + numero + ' · ' + (cita.am_pm || '');
      const m = L.marker([lat, lng], { icon: iconCita, zIndexOffset: seleccionada ? 900 : 0 })
        .bindTooltip(etiqueta, { permanent: false });
      m.on('click', () => this.seleccionarCita(cita));
      m.addTo(this.markersLayer);
    });

    if (bounds.length > 0) {
      this.map.fitBounds(bounds as any, { padding: [30, 30], maxZoom: 14 });
    }
  }

  seleccionarCita(cita: any) {
    this.citaSeleccionada = cita;
    // Re-pinta para resaltar el marker seleccionado
    if (this.map) { this.pintarMarcadores(); }
    // Centrar en la cita elegida
    const lat = Number(cita.lat_cita);
    const lng = Number(cita.lng_cita);
    if (!isNaN(lat) && !isNaN(lng) && this.map) {
      this.map.panTo([lat, lng]);
    }
  }

  asignar() {
    if (!this.citaSeleccionada) { return; }
    this.asignando = true;
    const user = this.localSave.get('usuarioPGV') || {};
    // Flag para que el backend solo marque asignacion_automatica=4 si la
    // cita venia de las 3 candidatas del SP. Si fue elegida del resto de
    // pendientes en el mapa, NO se toca asignacion_automatica.
    const esCandidata = this.citas.some(
      c => Number(c.id_cita) === Number(this.citaSeleccionada.id_cita)
    );
    this.gateway.get('autoAsignLejAsignar', {
      id_registro: this.data.id_registro,
      id_cita: this.citaSeleccionada.id_cita,
      id_tecnico: this.data.id_tecnico,
      idusuario: user.IDU || user.idagente,
      es_candidata: esCandidata
    }).then((res: any) => {
      this.asignando = false;
      if (Number(res.response) === 1) {
        this.snackbar.open('Cita asignada correctamente.', 'Cerrar', { duration: 2500 });
        this.dialogRef.close({ asignada: true });
      } else if (Number(res.response) === 2) {
        this.snackbar.open(res.message || 'La cita ya fue asignada a otro técnico.', 'Cerrar', { duration: 4000 });
        this.cargarTopCitas();  // refrescar las 3 citas
      } else {
        this.snackbar.open(res.message || 'No se pudo asignar.', 'Cerrar', { duration: 4000 });
      }
    }).catch(() => {
      this.asignando = false;
      this.snackbar.open('Error al asignar.', 'Cerrar', { duration: 3000 });
    });
  }

  /**
   * Regresa el registro a pendiente (atendido=0). Solo el duenio puede
   * cancelar; el backend valida. Al terminar cierra el dialog.
   */
  cancelar() {
    if (this.cancelando || this.anulando || this.asignando || this.esperando) { return; }
    const ref = this.dialog.open(ConfirmacionComponent, {
      disableClose: true,
      width: '350px',
      data: { sms: 'regresar esta alarma a pendiente', addcomment: false }
    });
    ref.afterClosed().subscribe((r: any) => {
      if (!r || !r.resp) { return; }
      this.cancelando = true;
      const user = this.localSave.get('usuarioPGV') || {};
      this.gateway.get('autoAsignLejCancelar', {
        id_registro: this.data.id_registro,
        idusuario: user.IDU || user.idagente
      }).then((res: any) => {
        this.cancelando = false;
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Alarma regresada a pendiente.', 'Cerrar', { duration: 2500 });
          this.dialogRef.close({ cancelada: true });
        } else {
          this.snackbar.open(res.message || 'No se pudo cancelar.', 'Cerrar', { duration: 4000 });
        }
      }).catch(() => {
        this.cancelando = false;
        this.snackbar.open('Error al cancelar.', 'Cerrar', { duration: 3000 });
      });
    });
  }

  /**
   * Marca el registro como anulado (atendido=4). Solo el duenio puede anular.
   */
  anular() {
    if (this.cancelando || this.anulando || this.asignando || this.esperando) { return; }
    const ref = this.dialog.open(ConfirmacionComponent, {
      disableClose: true,
      width: '350px',
      data: { sms: 'anular esta alarma', addcomment: false }
    });
    ref.afterClosed().subscribe((r: any) => {
      if (!r || !r.resp) { return; }
      this.anulando = true;
      const user = this.localSave.get('usuarioPGV') || {};
      this.gateway.get('autoAsignLejAnular', {
        id_registro: this.data.id_registro,
        idusuario: user.IDU || user.idagente
      }).then((res: any) => {
        this.anulando = false;
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Alarma anulada.', 'Cerrar', { duration: 2500 });
          this.dialogRef.close({ anulada: true });
        } else {
          this.snackbar.open(res.message || 'No se pudo anular.', 'Cerrar', { duration: 4000 });
        }
      }).catch(() => {
        this.anulando = false;
        this.snackbar.open('Error al anular.', 'Cerrar', { duration: 3000 });
      });
    });
  }

  /**
   * Pasa el registro al estado "Esperar cita cercana" (atendido=5). Libera
   * el idusuario para que cualquiera pueda re-tomarlo mas tarde.
   */
  esperarCitaCercana() {
    if (this.cancelando || this.anulando || this.asignando || this.esperando) { return; }
    const ref = this.dialog.open(ConfirmacionComponent, {
      disableClose: true,
      width: '380px',
      data: { sms: 'pasar esta alarma a "Esperar cita cercana"', addcomment: false }
    });
    ref.afterClosed().subscribe((r: any) => {
      if (!r || !r.resp) { return; }
      this.esperando = true;
      const user = this.localSave.get('usuarioPGV') || {};
      this.gateway.get('autoAsignLejEsperarCercana', {
        id_registro: this.data.id_registro,
        idusuario: user.IDU || user.idagente
      }).then((res: any) => {
        this.esperando = false;
        if (Number(res.response) === 1) {
          this.snackbar.open(res.message || 'Alarma en espera de cita cercana.', 'Cerrar', { duration: 2500 });
          this.dialogRef.close({ esperandoCercana: true });
        } else {
          this.snackbar.open(res.message || 'No se pudo cambiar el estado.', 'Cerrar', { duration: 4000 });
        }
      }).catch(() => {
        this.esperando = false;
        this.snackbar.open('Error al cambiar el estado.', 'Cerrar', { duration: 3000 });
      });
    });
  }

  cerrar() {
    this.dialogRef.close({ asignada: false });
  }
}
